import { Link } from 'react-router-dom';
import { useRecoilValue } from 'recoil';
import { activeClientState, meState, selectedFundState, reportCutoffState } from '../state.js';
import { useApi } from '../hooks.js';
import { AdvisorSummaryCard, usePublishedAdvisorSummary } from '../components/advisor/AdvisorSummaryBlock.js';
import { FundDropdown, type FundOption } from '../components/FundDropdown.js';
import { CutoffDropdown } from '../components/CutoffDropdown.js';
import { KpiCards, kpiFromNumbers } from '../components/KpiCards.js';
import { BenchmarkChart } from '../components/BenchmarkChart.js';
import { MonthlyReturnsChart } from '../components/MonthlyReturnsChart.js';
import { AllocationPie } from '../components/AllocationPie.js';
import { AssetVolumeChart, NetFlowChart } from '../components/NetFlowChart.js';
import { RikuzTable } from '../components/RikuzTable.js';
import { PositionsTable, type PositionRow } from '../components/PositionsTable.js';
import { ManagersMatrix } from '../components/ManagersMatrix.js';
import { LinkagePie, GeoPie } from '../components/BreakdownPies.js';
import { ManagersReturnsTable, ShekelChangesTable } from '../components/ManagersTables.js';
import { RiskAnalysis } from '../components/RiskAnalysis.js';
import { SectionNav } from '../components/SectionNav.js';
import { PolicyControlTable, policyHasData } from '../components/PolicyControlTable.js';
import { SkeletonKpis, SkeletonChart } from '../components/Skeleton.js';
import { PortfolioReturnChart, TotalAssetsChart, type PortfolioMonth, type PortfolioYearStat } from '../components/PortfolioSummaryCharts.js';
import { monthlyReturns } from '../utils.js';
import { withStatementReturns } from '../statementReturns.js';
import { FundCurrencyProvider, type FundCurrencyRow } from '../money.js';
import { activeFunds } from '../activeFunds.js';
import { onlyVisibleFunds } from '../visibleFunds.js';
import { portfolioFrom } from '../returnsFrom.js';
import type { ClientInfo, PerformanceRow, PolicyReportView, RikuzLineRow } from '@agio/core';
import type { AltInvestmentView } from '@agio/core/domain';
import { AltBenchmarkNote, AltReportProvider } from '../altReports.js';
import { AlternativeFundPanel } from '../components/AlternativeFundPanel.js';

interface BenchRow {
  date: string;
  fund_id: string;
  monthly_benchmark: number | null;
  cumulative_benchmark: number | null;
}

/** A figure and the month it belongs to, read off one row so the two cannot drift. */
type Dated = { value: number | null; date: string } | null;

/** The last row of a series, as its value and its own date. Null when empty. */
function lastOf<T extends { date: string }>(rows: T[], value: (r: T) => number | null): Dated {
  const row = rows.at(-1);
  return row ? { value: value(row), date: row.date } : null;
}

export function RikuzPage() {
  const me = useRecoilValue(meState);
  const clientId = useRecoilValue(activeClientState);
  const fundId = useRecoilValue(selectedFundState);
  const cutoff = useRecoilValue(reportCutoffState);

  const fundQ = fundId ? `fund=${encodeURIComponent(fundId)}` : '';
  const asOfQ = cutoff ? `asOf=${encodeURIComponent(cutoff)}` : '';
  const qs = (parts: string[]) => (parts.filter(Boolean).length ? `?${parts.filter(Boolean).join('&')}` : '');
  const { data: funds, loading: fundsLoading } = useApi<FundOption[]>(clientId ? `/clients/${clientId}/funds` : null);
  // The client's own settings — here, the first month its return charts show.
  const { data: clients } = useApi<ClientInfo[]>(clientId ? '/clients' : null);
  const returnsFrom = (clients ?? []).find((c) => c.client_id === clientId)?.returns_from_month ?? null;
  // Every fund's currency, hidden funds included — the summary is built over all of them.
  const { data: currencies } = useApi<FundCurrencyRow[]>(clientId ? `/clients/${clientId}/currencies` : null);
  const { data: rikuz } = useApi<RikuzLineRow[]>(clientId ? `/clients/${clientId}/rikuz${qs([fundQ, asOfQ])}` : null);
  const { data: perf, loading: perfLoading } = useApi<PerformanceRow[]>(clientId ? `/clients/${clientId}/performance${qs([fundQ, asOfQ])}` : null);
  const { data: bench } = useApi<BenchRow[]>(clientId ? `/clients/${clientId}/benchmark${qs([fundQ, asOfQ])}` : null);
  const { data: portfolio } = useApi<{ months: PortfolioMonth[]; years: PortfolioYearStat[] }>(
    clientId && !fundId ? `/clients/${clientId}/portfolio${qs([asOfQ])}` : null,
  );
  const { data: positions } = useApi<PositionRow[]>(
    clientId && fundId ? `/clients/${clientId}/positions${qs([fundQ, asOfQ])}` : null,
  );
  // Policy control is published per month; with a cutoff picked it is that month's,
  // otherwise the latest month published.
  const { data: policyView } = useApi<PolicyReportView | null>(
    clientId ? `/clients/${clientId}/policy${cutoff ? `?period=${encodeURIComponent(cutoff.slice(0, 7))}` : ''}` : null,
  );

  // Alternative investments and their published quarterly reports (the client's own, RLS-scoped).
  const { data: alternatives } = useApi<AltInvestmentView[]>(clientId ? `/clients/${clientId}/alternatives${qs([asOfQ])}` : null);

  // The advisor's published summary — not tied to the month picked; shown on the summary view only.
  const advisorSummary = usePublishedAdvisorSummary(clientId);

  if (!clientId) {
    return (
      <div className="py-24 text-center text-muted-foreground">
        {me?.role === 'uber' ? 'בחר לקוח מהסרגל העליון כדי לצפות בתיק.' : 'המשתמש אינו משויך ללקוח.'}
      </div>
    );
  }
  if (fundsLoading) {
    return (
      <div className="flex flex-col gap-6">
        <SkeletonKpis />
        <SkeletonChart height={320} />
        <SkeletonChart height={220} />
      </div>
    );
  }

  // Same defensive fund filter as the rikuz lines below: while the fund's own
  // rows are still loading, the previous (whole-client) rows must not be drawn
  // as if they were this fund's — two funds' months side by side under one key.
  const benchRows = (bench ?? []).filter((b) => !fundId || b.fund_id === fundId);
  // A month published from a statement has no workbook returns of its own —
  // they are worked out here, so the returns, benchmark and charts reach the
  // same month the balance does. Workbook months pass through unchanged.
  const perfRows = withStatementReturns((perf ?? []).filter((p) => !fundId || p.fund_id === fundId), benchRows);
  // Defensive fund filter kills the stale-data flash while switching funds
  // (the SERVER still scopes rows — this is purely render consistency).
  // With a cutoff picked, the server already returned each fund's own row AT OR
  // BEFORE that date — is_latest (the true, unbounded latest) no longer applies.
  const allLatest = (rikuz ?? []).filter((l) => (cutoff ? true : l.is_latest) && (!fundId || l.fund_id === fundId));
  // Summary counts only the client's CURRENT month: funds whose last snapshot is
  // older (inactive accounts) would otherwise inflate the totals.
  const maxDate = allLatest.reduce<string | null>((a, l) => (l.report_date && (!a || l.report_date > a) ? l.report_date : a), null);
  const lines = fundId ? allLatest : allLatest.filter((l) => l.report_date === maxDate);

  // KPI aggregation: single fund → its snapshot; whole portfolio → sum of latest snapshots
  const latestByFund = new Map<string, number>();
  for (const l of lines) latestByFund.set(l.holding_id, l.total_value ?? 0);
  const rikuzTotal = [...latestByFund.values()].reduce((a, b) => a + b, 0) || null;
  // ריכוז only ever carries a fund's CURRENT holdings snapshot, not one per month — so
  // at an earlier cutoff it usually has nothing yet. Fall back to the asset-volume series
  // (fund_performance / the whole-portfolio series), which IS a real monthly history, same
  // source the assets chart already uses. Only fills the gap; never overrides a real value.
  const fallbackTotal = fundId ? (perfRows.at(-1)?.asset_volume ?? null) : (portfolio?.months.at(-1)?.total_volume ?? null);
  const totalValue = rikuzTotal ?? fallbackTotal;
  // The funds behind that total — from the same source, so a past month with
  // no snapshot no longer reads "0 active" under a real total.
  // While loading, the previous month's rows are still in `perf` — not usable here.
  const portfolioFunds = activeFunds(lines, perfLoading || perf === null ? null : perfRows, portfolio?.months.at(-1)?.date ?? null);

  // Whole-portfolio view: the per-fund tables (and their total rows) leave out
  // funds an admin switched off; the headline figures above still count every fund.
  // Fund view is one fund's own rows — never filtered, so a fund switched off
  // while it is on screen keeps its charts beside its holdings.
  const activePerf = fundId ? perfRows : onlyVisibleFunds(perfRows, funds ?? []);
  const rets = monthlyReturns(activePerf).filter((r) => r.ret !== null);
  // Summary view: KPIs come from the whole-portfolio series (organic aggregate);
  // fund view: from that fund's own series.
  const pm = (portfolio?.months ?? []).filter((m) => m.monthly_return !== null);
  const extractedE = activePerf.filter((p) => p.monthly_return !== null);
  const extractedH = activePerf.filter((p) => p.benchmark_monthly !== null);
  // Each headline figure is picked together with the month it belongs to, from
  // the SAME row. They used to be chosen separately — the value from one list
  // and the date from a shared `asOf` that fell back to the balance's month —
  // so a March return could be captioned April, and an April balance March.
  const lastRet = fundId
    ? (lastOf(extractedE, (p) => p.monthly_return) ?? lastOf(rets, (r) => r.ret))
    : lastOf(pm, (m) => m.monthly_return);
  const lastBench = fundId
    ? (lastOf(extractedH, (p) => p.benchmark_monthly)
       ?? lastOf(benchRows.filter((b) => b.monthly_benchmark !== null), (b) => b.monthly_benchmark))
    : lastOf((portfolio?.months ?? []).filter((m) => m.benchmark_monthly !== null), (m) => m.benchmark_monthly);

  // For the whole-portfolio view, charts need a single fund's series to be meaningful;
  // pick the aggregate only when a specific fund is selected.
  const chartsPerf = fundId ? activePerf : [];
  const chartsBench = fundId ? benchRows : [];

  // Three figures, three months. A balance moves as soon as a statement is
  // published; a return only once that month's deposits and withdrawals are
  // confirmed. Falling back to the balance's month for a missing return was how
  // one month's money ended up wearing another month's name.
  const asOf = lastRet?.date ?? null;
  const benchAsOf = lastBench?.date ?? null;
  const balanceAsOf = maxDate;
  // Fund view: this fund's active span (earliest→latest across performance AND
  // ריכוז snapshots). Still-active funds (not ישן) show "היום".
  const fundDates = [
    ...activePerf.map((p) => p.date),
    ...(rikuz ?? []).map((l) => l.report_date),
  ]
    .filter((d): d is string => !!d)
    .sort();
  const selectedFund = (funds ?? []).find((f) => f.fund_id === fundId);
  // An alternative investment's own page: quarterly history instead of a benchmark chart and securities.
  const isAlt = selectedFund?.kind === 'alternative';
  const hasAlternatives = (alternatives ?? []).some((a) => a.statements.length > 0);
  // In fund view the 4th KPI is always this fund's span (never a trivial "1 fund");
  // when the fund carries no dated data at all, the label falls back to "—".
  const period: { from: string | null; to: string | null } | null = fundId
    ? {
        from: fundDates[0] ?? null,
        to: fundDates.length && selectedFund?.is_old ? fundDates[fundDates.length - 1] : null,
      }
    : null;

  // Policy control appears only once a month has been published for this client —
  // an empty "not published yet" card is noise to a client who has no policy set up.
  const hasPolicy = policyHasData(policyView) && (!fundId || policyView.results.some((r) => r.fund_id === fundId));
  const sections = fundId
    ? [
        { id: 'top', label: 'סיכום' },
        isAlt ? { id: 'alternative', label: 'דוחות רבעוניים' } : { id: 'benchmark', label: 'ביצועים מול מדד' },
        { id: 'monthly', label: 'תשואה חודשית והיקף' },
        { id: 'flows', label: 'תזרים מזומנים' },
        { id: 'rikuz', label: 'ריכוז' },
        { id: 'allocation', label: 'הקצאת נכסים' },
        ...(isAlt ? [] : [{ id: 'positions', label: 'פוזיציות' }]),
        ...(hasPolicy ? [{ id: 'policy', label: 'בקרת מדיניות' }] : []),
      ]
    : [
        { id: 'top', label: 'סיכום' },
        { id: 'performance', label: 'תשואת התיק' },
        { id: 'managers', label: 'מנהלים' },
        { id: 'composition', label: 'הרכב תיק' },
        { id: 'risk', label: 'ניתוח סיכון' },
        { id: 'rikuz', label: 'ריכוז' },
        ...(hasPolicy ? [{ id: 'policy', label: 'בקרת מדיניות' }] : []),
      ];

  return (
    // Two columns from `xl` up: the side menu is a real column of the page, so
    // it never sits on top of the content it is meant to navigate.
    <FundCurrencyProvider funds={currencies ?? []}>
    <AltReportProvider perf={perfRows}>
    <div className="xl:grid xl:grid-cols-[12rem_minmax(0,1fr)] xl:gap-8">
      <SectionNav sections={sections} />
      <div className="flex min-w-0 flex-col gap-6">
        <div id="top" className="flex flex-wrap min-w-0 items-center gap-2 sm:gap-4">
          <h1 className="text-2xl font-bold">תיק השקעות</h1>
          <div className="flex-1 min-w-[200px]"><FundDropdown funds={funds ?? []} /></div>
          <CutoffDropdown clientId={clientId} />
          <Link
            to="/print"
            className="rounded-md bg-primary px-3 sm:px-4 py-1.5 text-sm font-semibold text-primary-foreground transition-opacity hover:opacity-90 whitespace-nowrap"
          >
            🖨 הדפסה
          </Link>
        </div>

        {!fundId && <AdvisorSummaryCard state={advisorSummary} />}

        <KpiCards
          kpis={kpiFromNumbers({
            totalValue,
            monthlyReturn: lastRet?.value ?? null,
            monthlyBenchmark: lastBench?.value ?? null,
            asOf,
            benchAsOf,
            balanceAsOf,
            fundCount: fundId ? 1 : (portfolioFunds?.length ?? null),
            fundNames: fundId ? undefined : portfolioFunds?.map((f) => f.name),
            period,
            currency: selectedFund?.currency,
          })}
        />

        {fundId ? (
          <>
            {isAlt ? (
              <>
                <p id="alternative" className="section-label">דוחות רבעוניים</p>
                <AlternativeFundPanel
                  fundId={fundId}
                  perf={chartsPerf}
                  investment={(alternatives ?? []).find((a) => a.fund_id === fundId) ?? null}
                />
              </>
            ) : (
              <>
                <p id="benchmark" className="section-label">ביצועים מול מדד</p>
                <BenchmarkChart perf={chartsPerf} bench={chartsBench} from={returnsFrom} />
              </>
            )}
            <p id="monthly" className="section-label">תשואה חודשית והיקף</p>
            <div className="grid gap-6 md:grid-cols-2">
              <MonthlyReturnsChart perf={chartsPerf} from={returnsFrom} />
              <AssetVolumeChart perf={chartsPerf} />
            </div>
            <p id="flows" className="section-label">תזרים מזומנים</p>
            <NetFlowChart perf={chartsPerf} />
            <p id="rikuz" className="section-label">ריכוז</p>
            <RikuzTable lines={lines} />
            <p id="allocation" className="section-label">הקצאת נכסים</p>
            <AllocationPie lines={lines} />
            {!isAlt && (
              <>
                <p id="positions" className="section-label">פוזיציות</p>
                <PositionsTable positions={(positions ?? []).filter((p) => p.fund_id === fundId)} />
              </>
            )}
            {hasPolicy && (
              <>
                <p id="policy" className="section-label">בקרת מדיניות</p>
                <PolicyControlTable view={policyView ?? null} fundId={fundId} />
              </>
            )}
          </>
        ) : (
          <>
            <p id="performance" className="section-label">תשואת התיק</p>
            <PortfolioReturnChart {...portfolioFrom(portfolio?.months ?? [], portfolio?.years ?? [], returnsFrom)} />
            {hasAlternatives && <AltBenchmarkNote />}
            <TotalAssetsChart months={portfolio?.months ?? []} />
            <p id="managers" className="section-label">מנהלים</p>
            <ManagersMatrix lines={onlyVisibleFunds(lines, funds ?? [])} />
            <ManagersReturnsTable perf={activePerf} />
            <ShekelChangesTable perf={activePerf} />
            <p id="composition" className="section-label">הרכב תיק</p>
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              <AllocationPie lines={aggregateLines(lines)} />
              <LinkagePie lines={aggregateLines(lines)} />
              <GeoPie lines={aggregateLines(lines)} />
            </div>
            <p id="risk" className="section-label">ניתוח סיכון</p>
            <RiskAnalysis perf={activePerf} />
            <p id="rikuz" className="section-label">ריכוז</p>
            <RikuzTable lines={aggregateLines(lines)} />
            {hasPolicy && (
              <>
                <p id="policy" className="section-label">בקרת מדיניות</p>
                <PolicyControlTable view={policyView ?? null} />
              </>
            )}
          </>
        )}
      </div>
    </div>
    </AltReportProvider>
    </FundCurrencyProvider>
  );
}

/** Whole-portfolio view: sum sleeve values across funds' latest snapshots. */
function aggregateLines(lines: RikuzLineRow[]): RikuzLineRow[] {
  if (lines.length === 0) return [];
  const bySleeve = new Map<string, RikuzLineRow>();
  let total = 0;
  for (const l of lines) {
    const cur = bySleeve.get(l.sleeve);
    if (!cur) bySleeve.set(l.sleeve, { ...l, report_id: `agg|${l.sleeve}`, fund: 'סיכום תיק', fund_id: 'agg' });
    else cur.value = (cur.value ?? 0) + (l.value ?? 0);
  }
  const sleeves = [...bySleeve.values()].sort((a, b) => a.sleeve_order - b.sleeve_order);
  total = sleeves.reduce((a, s) => a + (s.value ?? 0), 0);
  return sleeves.map((s) => ({ ...s, total_value: total, pct_of_total: total ? (s.value ?? 0) / total : null }));
}
